import { useState, type ReactNode } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { NAV, type NavNode } from '@/config/nav';
import { useAuth } from '@/lib/auth/useAuth';
import { roleHas } from '@/lib/rbac/permissions';
import { useLocale } from '@/lib/i18n/useLocale';
import { cn } from '@/lib/ui/cn';
import { AccountMenu } from '@/features/auth/AccountMenu';
import { GlobalSearch } from '@/features/search/GlobalSearch';

/**
 * Two-tier shell: a Why-White sidebar on the cream ground + a thin top utility strip.
 * Charcoal-and-cream by default (DESIGN "one yellow step"): the active nav item is
 * marked by weight plus a single Direction-Yellow tick, the only accent in the chrome.
 * RTL-native via logical properties; nav renders from config + RBAC.
 */
export function AppShell({ children }: { children: ReactNode }) {
  const { t } = useTranslation();
  const { role } = useAuth();
  const [mobileOpen, setMobileOpen] = useState(false);

  const visibleNodes = NAV.map((node) => filterNode(node, role)).filter(Boolean) as NavNode[];

  return (
    <div className="flex min-h-screen bg-canvas-sand">
      {/* Sidebar */}
      <aside
        className={cn(
          'fixed inset-y-0 start-0 z-40 flex w-[248px] flex-col border-e border-hairline bg-canvas-paper text-ink-mid transition-transform duration-almanac ease-almanac lg:sticky lg:top-0 lg:h-screen lg:translate-x-0',
          mobileOpen
            ? 'translate-x-0'
            : '-translate-x-full rtl:translate-x-full lg:translate-x-0 lg:rtl:translate-x-0',
        )}
      >
        <div className="flex items-center gap-sm border-b border-hairline px-lg py-md">
          <span className="font-mono text-label uppercase tracking-[0.12em] text-ink-deep">Y/</span>
          <span className="text-component-title font-medium text-ink-deep">{t('app.title')}</span>
        </div>
        <nav className="flex-1 overflow-y-auto px-sm py-md" aria-label={t('app.primaryNav')}>
          {visibleNodes.map((node) => (
            <NavRenderer key={node.navKey} node={node} onNavigate={() => setMobileOpen(false)} />
          ))}
        </nav>
        <div className="border-t border-hairline px-lg py-sm">
          <p className="font-mono text-label-sm uppercase tracking-[0.12em] text-ink-mute">
            {t('app.edition')}
          </p>
          {/* The build's version, so an editor reporting an issue can say what they ran. */}
          <p className="mt-xs font-mono text-label-sm text-ink-mute" title={__APP_COMMIT__}>
            {t('app.version')} <bdi dir="ltr">{__APP_VERSION__}</bdi>
          </p>
        </div>
      </aside>

      {/* Main column */}
      <div className="flex min-w-0 flex-1 flex-col">
        <TopBar onToggleMobile={() => setMobileOpen((v) => !v)} />
        <main className="mx-auto w-full max-w-[1280px] flex-1 p-lg lg:p-xl">{children}</main>
      </div>
    </div>
  );
}

function TopBar({ onToggleMobile }: { onToggleMobile: () => void }) {
  const { t } = useTranslation();
  const { locale, setLocale } = useLocale();
  return (
    <header className="sticky top-0 z-30 flex items-center gap-md border-b-[0.5px] border-hairline bg-canvas-paper px-lg py-sm">
      <button
        className="rounded-sm px-xs text-ink-deep lg:hidden focus-visible:shadow-focus"
        onClick={onToggleMobile}
        aria-label={t('app.toggleNav')}
      >
        ☰
      </button>
      <div className="flex-1">
        <GlobalSearch />
      </div>
      {/* Language toggle — Canvas Sand pill, announces switch (DESIGN §5 Navigation). */}
      <button
        onClick={() => setLocale(locale === 'en' ? 'ar' : 'en')}
        aria-live="polite"
        className="rounded-pill border-[0.5px] border-hairline bg-canvas-sand px-[10px] py-[5px] font-mono text-label uppercase tracking-[0.1em] text-ink-deep hover:border-accent-blue focus-visible:shadow-focus"
      >
        {locale === 'en' ? 'عربي' : 'EN'}
      </button>
      <AccountMenu />
    </header>
  );
}

function NavRenderer({ node, onNavigate }: { node: NavNode; onNavigate: () => void }) {
  const { t } = useTranslation();
  if (node.type === 'leaf') {
    return <NavLink to={node.to} label={t(`nav.${node.navKey}`)} onNavigate={onNavigate} />;
  }
  return (
    <div className="mb-sm mt-md">
      <p className="px-sm py-xs font-mono text-label-sm uppercase tracking-[0.12em] text-ink-mute">
        {t(`nav.${node.navKey}`)}
      </p>
      {node.children.map((leaf) => (
        <NavLink key={leaf.navKey} to={leaf.to} label={t(`nav.${leaf.navKey}`)} onNavigate={onNavigate} />
      ))}
    </div>
  );
}

function NavLink({ to, label, onNavigate }: { to: string; label: string; onNavigate: () => void }) {
  const { pathname } = useLocation();
  const active = to === '/' ? pathname === '/' : pathname.startsWith(to);
  return (
    <Link
      to={to}
      onClick={onNavigate}
      className={cn(
        'relative flex items-center rounded-sm px-sm py-[9px] text-small transition-colors duration-almanac',
        active
          ? 'bg-canvas-sand font-semibold text-ink-deep before:absolute before:inset-y-[6px] before:start-0 before:w-[2px] before:bg-sky-blue'
          : 'text-ink-mid hover:bg-canvas-bone hover:text-ink-deep',
      )}
    >
      {label}
    </Link>
  );
}

/** Hide nodes the role cannot read; drop empty groups. */
function filterNode(node: NavNode, role: ReturnType<typeof useAuth>['role']): NavNode | null {
  if (node.type === 'leaf') return roleHas(role, node.permission) ? node : null;
  const children = node.children.filter((c) => roleHas(role, c.permission));
  return children.length ? { ...node, children } : null;
}
